<template>
	<div class="flow-config-panel display-flex flex-column">
		<AppCloseX class="close-icon" @onClose="close" />

		<div class="top-section">
			<ModuleConfigurationBar :flowData="editWorkflow" />
		</div>
		<div class="middle-section flex-1">
			<div class="middle-content" v-if="isOfTypeFeature">
				<div class="content-title">Share</div>
				<div class="content-config flex-space-between--center">
					<div>Only you can see this</div>
					<div>
						<ShareButton :disabled="!hasSharePermissions" :workflowId="editWorkflow.id" class="config-btn" dense
							>Open sharing options</ShareButton
						>
					</div>
				</div>
			</div>
			<div class="middle-content" v-if="isURLToolPlacement">
				<div class="content-title">Get the link</div>
				<div class="content-config flex-start--center">
					<div class="mr-3">
						<AppTextField class="url-input" outlined dense readonly :value="flowRunUrl" hide-details single-line />
					</div>
					<div class="inline-link-style ml-3" @click="copyUrl">Copy Link</div>
				</div>
			</div>
			<div class="middle-content" v-if="isInstallablesOn">
				<template v-if="!selectedWorkflowInstallable">
					<div class="content-title">Make an installable</div>
					<div class="content-config flex-start--center pt-2">
						<AnemicButton :disabled="!hasEditPermissions" @click="createInstallableForSelectedWorkflow" :loading="loadingCreateInstallable"
							>Create</AnemicButton
						>
					</div>
				</template>
				<template v-else>
					<FlowConfigPanelInstallableCard />
				</template>
			</div>
		</div>
		<div class="bottom-section inline-link-style" @click="close">Close and go back to module</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { editWorkflow, isOfTypeTool, isOfTypeFeature, getSelectedWorkflowInstallable } from '@/store/modules/selectedWorkflow/getters';
import { createInstallableForSelectedWorkflow } from '@/store/modules/selectedWorkflow/actions';
import { isInstallablesOn } from '@/store/modules/featureFlags/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { projectHasPermission } from '@/store/modules/projects/getters';

import ModuleConfigurationBar from '@/components/Modules/common/ModuleConfigurationBar';
import ShareButton from './share/ShareButton';
import FlowConfigPanelInstallableCard from './FlowConfigPanelInstallableCard';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowToolService from '@/utils/workflow-tool-service';
import WorkflowService from '@/utils/WorkflowService';

export default {
	name: 'FlowConfigPanel',
	components: {
		ModuleConfigurationBar,
		ShareButton,
		FlowConfigPanelInstallableCard,
	},
	computed: {
		...mapGetters([
			editWorkflow.fullPath,
			isOfTypeFeature.fullPath,
			getSelectedWorkflowInstallable.fullPath,
			isInstallablesOn.fullPath,
			canEditWorkflow.fullPath,
		]),
		...mapGettersShorthand({ editWorkflow, isOfTypeTool }),
		isOfTypeFeature() {
			return this[isOfTypeFeature.fullPath];
		},
		selectedWorkflowInstallable() {
			return this[getSelectedWorkflowInstallable.fullPath];
		},
		isInstallablesOn() {
			return this[isInstallablesOn.fullPath];
		},
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this.editWorkflow?.id);
		},
		hasSharePermissions() {
			return this.hasEditPermissions || this[projectHasPermission.fullPath](this.flowData.project, ProjectPermissions.ChangeWorkflowUsers);
		},
		isURLToolPlacement() {
			return this.isOfTypeTool && WorkflowToolService.isURLTool(this.editWorkflow);
		},
		flowRunUrl() {
			return WorkflowService.getRunLink(this.editWorkflow);
		},
	},
	methods: {
		...mapActions([createInstallableForSelectedWorkflow.fullPath]),
		async createInstallableForSelectedWorkflow() {
			let ret;
			try {
				this.loadingCreateInstallable = true;
				ret = await this[createInstallableForSelectedWorkflow.fullPath]();
			} catch (e) {
				if (e?.response?.data?.error?.msg) {
					this.$toast.error(e?.response?.data?.error?.msg);
				}
			} finally {
				this.loadingCreateInstallable = false;
			}
			console.log('ret is', ret);
		},
		close() {
			this.$emit('close');
		},
		copyUrl() {
			navigator.clipboard.writeText(this.flowRunUrl);
			this.$toast.success('Copied URL');
		},
	},
	data() {
		return { loadingCreateInstallable: false };
	},
};
</script>

<style scoped lang="scss">
.flow-config-panel {
	$side-pad: 10px;

	position: relative;
	width: 370px;
	background-color: var(--app-dialog-bg);
	border-radius: 8px;
	border: 1px solid #e7eaf3;
	box-shadow: var(--box-shadow-dark);

	.close-icon {
		position: absolute;
		height: 26px;
		top: -12px;
		right: -12px;
	}
	.top-section {
		padding: $side-pad;
		border-bottom: 1px solid #e7eaf3;
		background-color: #f5f7fa;
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}
	.middle-section {
		text-align: left;

		.middle-content {
			margin: $side-pad;
			padding: 10px 0;

			&:not(:first-of-type) {
				border-top: 1px solid #e7eaf3;
			}
			.content-title {
				font-size: 14px;
				font-weight: 700;
			}
			.content-config {
				font-size: 12px;
			}
		}
	}
	.bottom-section {
		padding: 10px $side-pad;
		border-top: 1px solid #e7eaf3;
	}

	.url-input {
		border-radius: 8px;
		width: 260px;
		::v-deep .v-text-field__slot {
			font-size: 12px;
		}
	}
	.inline-link-style {
		color: var(--primary-color);
	}

	.config-btn {
		border-color: #e7eaf3;
		::v-deep .v-btn__content {
			color: var(--forth-color);
		}
	}
}
</style>
